import React, { useEffect, useState, useMemo } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  X,
  RefreshCw,
  Send,
  Sparkles,
  AlertTriangle,
  MessageSquare,
  Mail,
  Phone,
  CheckCircle2,
  Clock,
  ExternalLink,
} from "lucide-react";
import { crmContactUrl } from "../../lib/crmLinks";
const BAND_STYLES = {
  hot: "bg-rose-500/10 border-rose-500/30 text-rose-300",
  warm: "bg-amber-500/10 border-amber-500/30 text-amber-300",
  cool: "bg-sky-500/10 border-sky-500/30 text-sky-300",
  cold: "bg-zinc-800/40 border-zinc-700 text-zinc-400",
};

function channelIcon(ch) {
  const c = (ch || "").toLowerCase();
  if (c.includes("email")) return <Mail className="w-3 h-3" />;
  if (c.includes("sms")) return <MessageSquare className="w-3 h-3" />;
  return <MessageSquare className="w-3 h-3" />;
}

function fmtTs(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function ContactDetailPanel({ contact, connection, onClose, onScoreUpdated }) {
  const [timeline, setTimeline] = useState(null);
  const [scoreRow, setScoreRow] = useState(contact);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [draftLoading, setDraftLoading] = useState(false);
  const [channel, setChannel] = useState("WhatsApp");
  const [sending, setSending] = useState(false);
  const [sentOk, setSentOk] = useState(false);
  const [aisensyConn, setAisensyConn] = useState(null);        // resolved active connection object
  const [aisensyList, setAisensyList] = useState([]);          // full list
  const [aisensyParams, setAisensyParams] = useState([]);
  const [aisensyPrefilling, setAisensyPrefilling] = useState(false);
  const [aisensyMessages, setAisensyMessages] = useState([]);

  const loadTimeline = async (refresh = false) => {
    setLoading(true);
    try {
      const r = await api.get(`/employz/contacts/${contact.ghl_contact_id}/timeline`, {
        params: refresh ? { refresh: true } : {},
      });
      setTimeline(r.data.timeline);
      setScoreRow(r.data.score_row);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load conversation.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTimeline(false);
    // Load the list of AiSensy connections + default to most-recently-used for this contact.
    Promise.all([
      api.get("/aisensy/connections").then((r) => r.data?.connections || []).catch(() => []),
      api.get(`/contacts/${contact.ghl_contact_id}/aisensy-last-used`).then((r) => r.data?.aisensy_connection_id).catch(() => null),
    ]).then(([list, lastId]) => {
      setAisensyList(list);
      if (!list.length) {
        setAisensyConn({ status: "not_connected" });
        return;
      }
      const preferred = (lastId && list.find((c) => c.id === lastId)) || list[0];
      setAisensyConn({ ...preferred, status: "connected" });
      setAisensyParams(Array(preferred.param_count || 0).fill(""));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.ghl_contact_id]);

  // Auto-draft once we have both a score and timeline
  useEffect(() => {
    if (!scoreRow?.score || draft || draftLoading || !timeline) return;
    setDraftLoading(true);
    api
      .post(`/employz/contacts/${contact.ghl_contact_id}/draft-reply`)
      .then((r) => setDraft(r.data.draft || ""))
      .catch(() => {})
      .finally(() => setDraftLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoreRow?.score, timeline]);

  const send = async () => {
    if (!draft.trim()) {
      toast.error("Message can't be empty.");
      return;
    }
    setSending(true);
    setSentOk(false);
    try {
      await api.post(`/employz/contacts/${contact.ghl_contact_id}/send-message`, {
        message: draft.trim(),
        channel,
      });
      setSentOk(true);
      setDraft("");
      toast.success(`Sent — logged in your CRM conversation.`);
      // Reload timeline to show the sent message
      setTimeout(() => loadTimeline(true), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  const hoursSince = timeline?.hours_since_last_inbound;
  const over24h = hoursSince == null || hoursSince > 24;
  const hasGhlSendScope = !!connection?.send_scope;
  const hasAisensy = aisensyList.length > 0;

  // Three-way router:
  //  inside 24h + GHL WhatsApp available → GHL free-form
  //  outside 24h (or GHL free-form unavailable) + AiSensy connected → AiSensy template
  //  neither → muted "programme team" line
  //  (SMS via GHL is always available as a manual toggle when GHL send-scope is on)
  const mode = useMemo(() => {
    if (!over24h && hasGhlSendScope) return "ghl_freeform";
    if (hasAisensy) return "aisensy_template";
    if (hasGhlSendScope) return "ghl_freeform";
    return "none";
  }, [over24h, hasGhlSendScope, hasAisensy]);

  const switchAisensyAccount = (connId) => {
    const next = aisensyList.find((c) => c.id === connId);
    if (!next) return;
    setAisensyConn({ ...next, status: "connected" });
    setAisensyParams(Array(next.param_count || 0).fill(""));
  };

  // Prefill AiSensy params in template mode
  useEffect(() => {
    if (mode !== "aisensy_template" || !aisensyConn?.id || !aisensyConn?.param_count) return;
    if (aisensyParams.length === aisensyConn.param_count && aisensyParams.some((p) => p)) return;
    setAisensyPrefilling(true);
    api
      .post(`/contacts/${contact.ghl_contact_id}/aisensy-draft`, { aisensy_connection_id: aisensyConn.id })
      .then((r) => setAisensyParams((r.data.params || []).slice(0, aisensyConn.param_count)))
      .catch(() => setAisensyParams(Array(aisensyConn.param_count).fill("")))
      .finally(() => setAisensyPrefilling(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, aisensyConn?.id, aisensyConn?.param_count]);

  const sendAisensy = async () => {
    if (aisensyParams.some((p) => !p?.trim())) {
      toast.error("Fill every variable slot before sending.");
      return;
    }
    setSending(true);
    setSentOk(false);
    try {
      await api.post(`/contacts/${contact.ghl_contact_id}/send-aisensy`, {
        params: aisensyParams,
        aisensy_connection_id: aisensyConn?.id,
      });
      setSentOk(true);
      toast.success(`Sent via AiSensy (${aisensyConn?.label || "account"}) — logged in your timeline.`);
      setTimeout(() => loadTimeline(true), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AiSensy send failed.");
    } finally {
      setSending(false);
    }
  };

  const threads = useMemo(() => timeline?.threads || [], [timeline]);

  return (
    <div
      data-testid="contact-detail-panel"
      className="fixed inset-0 z-40 flex justify-end"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-2xl h-full bg-zinc-950 border-l border-zinc-800 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-zinc-900 px-5 py-4 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl font-black text-white truncate">
              {scoreRow?.name || contact.name || contact.ghl_contact_id}
            </div>
            <div className="text-[12px] text-zinc-500 truncate">
              {contact.phone || "—"} {contact.email ? `· ${contact.email}` : ""}
            </div>
          </div>
          {connection?.location_id && contact.ghl_contact_id && (
            <a
              href={crmContactUrl(connection?.ui_base_url, connection.location_id, contact.ghl_contact_id)}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-open-in-crm"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] px-2.5 py-1.5 rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700 transition-colors"
              title="Open this contact in the CRM"
            >
              <ExternalLink className="w-3 h-3" /> Open in CRM
            </a>
          )}
          <button
            data-testid="contact-panel-close"
            onClick={onClose}
            className="p-2 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Section A — Score summary */}
          {scoreRow?.score != null ? (
            <section data-testid="panel-score-summary" className="rounded-2xl border border-zinc-900 bg-zinc-950/60 p-4">
              <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-2">Score summary</div>
              <div className="flex items-baseline gap-3">
                <div className="font-display text-4xl font-black text-white">{scoreRow.score}</div>
                <div className="text-xs text-zinc-500">/100</div>
                <div className={`text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full border ${BAND_STYLES[scoreRow.band] || BAND_STYLES.cool}`}>
                  {scoreRow.band}
                </div>
              </div>
              <div className="mt-2 text-sm text-zinc-300 leading-relaxed">{scoreRow.why}</div>
              {scoreRow.signals?.length > 0 && (
                <ul className="mt-3 space-y-1 text-[13px] text-zinc-400">
                  {scoreRow.signals.map((s, i) => (
                    <li key={i} className="flex gap-2"><span className="text-zinc-600">·</span><span>{s}</span></li>
                  ))}
                </ul>
              )}
              {scoreRow.recommended_action && (
                <div className="mt-3 text-sm text-emerald-300/90 border-l-2 border-emerald-500/30 pl-3">
                  {scoreRow.recommended_action}
                </div>
              )}
              <div className="mt-4 text-[11px] uppercase tracking-[0.18em] text-emerald-400 inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Written to CRM — AI Lead Score field
              </div>
            </section>
          ) : (
            <section className="rounded-2xl border border-zinc-900 bg-zinc-950/60 p-4">
              <div className="text-sm text-zinc-500">
                This lead hasn&apos;t been scored yet. Close this panel and hit &quot;Score&quot; on the row.
              </div>
            </section>
          )}

          {/* Section B — Timeline */}
          <section data-testid="panel-timeline">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">What happened</div>
              <button
                data-testid="panel-timeline-refresh"
                onClick={() => loadTimeline(true)}
                disabled={loading}
                className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
              </button>
            </div>
            <div className="rounded-2xl border border-zinc-900 bg-zinc-950/60 p-3 max-h-[45vh] overflow-y-auto space-y-2">
              {loading && <div className="text-sm text-zinc-500 p-3">Loading conversation…</div>}
              {!loading && threads.length === 0 && (
                <div className="text-sm text-zinc-500 p-3">No conversation history yet.</div>
              )}
              {threads.map((m, i) => {
                const isIn = (m.direction || "") === "inbound";
                return (
                  <div key={i} className={`flex ${isIn ? "justify-start" : "justify-end"}`}>
                    <div
                      className={`max-w-[80%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed ${
                        isIn ? "bg-zinc-900 text-zinc-200" : "bg-emerald-950/40 border border-emerald-500/20 text-emerald-100"
                      }`}
                    >
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] mb-1 opacity-70">
                        {channelIcon(m.channel)} {m.channel || "message"} · {fmtTs(m.ts)}
                      </div>
                      <div className="whitespace-pre-wrap">{m.text || ""}</div>
                    </div>
                  </div>
                );
              })}
              {(timeline?.opportunities || []).map((o, i) => (
                <div key={`opp-${i}`} className="mx-4 my-2 text-center">
                  <div className="inline-block text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full border border-amber-500/30 bg-amber-500/5 text-amber-300">
                    {o.name} — {o.stage} {o.monetaryValue ? `· ₹${o.monetaryValue.toLocaleString()}` : ""}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section C — Send a reply (three-way router) */}
          <section data-testid="panel-reply">
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-2">Send a reply</div>

            {mode === "none" && (
              <div className="rounded-2xl border border-zinc-900 bg-zinc-950/60 p-4 text-sm text-zinc-400" data-testid="send-scope-missing">
                Sending isn&apos;t enabled on this connection yet — your programme team is on it.
              </div>
            )}

            {mode === "ghl_freeform" && (
              <div className="rounded-2xl border border-zinc-900 bg-zinc-950/60 p-4" data-testid="composer-ghl-freeform">
                {over24h && (
                  <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-[12px] text-amber-200/90 inline-flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <div>
                      WhatsApp only delivers free-form replies within 24h of the customer&apos;s last message
                      {hoursSince != null ? ` (${Math.round(hoursSince)}h ago)` : ""}. Consider SMS
                      {hasAisensy ? " or an AiSensy approved template" : ""}.
                    </div>
                  </div>
                )}
                <textarea
                  data-testid="reply-textarea"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={4}
                  placeholder={draftLoading ? "Drafting your reply…" : "Type or paste a message…"}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-emerald-500/40"
                />
                {draftLoading && (
                  <div className="mt-1 text-[11px] text-zinc-500 inline-flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3 animate-pulse" /> Generating AI draft…
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                  <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                    Channel
                    <div className="inline-flex rounded-md border border-zinc-800 overflow-hidden">
                      <button data-testid="channel-whatsapp" onClick={() => setChannel("WhatsApp")}
                        className={`px-2 py-1 ${channel === "WhatsApp" ? "bg-emerald-500/15 text-emerald-300" : "text-zinc-500"}`}>WhatsApp</button>
                      <button data-testid="channel-sms" onClick={() => setChannel("SMS")}
                        className={`px-2 py-1 border-l border-zinc-800 ${channel === "SMS" ? "bg-emerald-500/15 text-emerald-300" : "text-zinc-500"}`}>SMS</button>
                    </div>
                  </div>
                  <button
                    data-testid="send-reply-btn"
                    onClick={send}
                    disabled={sending || !draft.trim()}
                    className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
                  >
                    <Send className="w-3.5 h-3.5" />
                    {sending ? "Sending…" : `Send ${channel}`}
                  </button>
                </div>
                {sentOk && (
                  <div className="mt-2 text-[11px] text-emerald-400 uppercase tracking-[0.18em] inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Sent — logged in your CRM conversation.
                  </div>
                )}
              </div>
            )}

            {mode === "aisensy_template" && aisensyConn?.template_parts && (
              <div className="rounded-2xl border border-zinc-900 bg-zinc-950/60 p-4" data-testid="composer-aisensy-template">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-300/80 inline-flex items-center gap-1.5">
                    <MessageSquare className="w-3 h-3" /> AiSensy · {aisensyConn.campaign_name}
                  </div>
                  {aisensyList.length > 1 && (
                    <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                      Account
                      <select
                        data-testid="aisensy-account-select"
                        value={aisensyConn.id}
                        onChange={(e) => switchAisensyAccount(e.target.value)}
                        className="bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1 text-emerald-300 text-[11px] focus:outline-none focus:border-emerald-500/40"
                      >
                        {aisensyList.map((c) => (
                          <option key={c.id} value={c.id}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                {over24h && (
                  <div className="mb-3 text-[12px] text-zinc-500">
                    Outside the 24h WhatsApp window {hoursSince != null ? `(${Math.round(hoursSince)}h since last reply)` : ""} — using an approved template.
                  </div>
                )}
                {/* Read-only template with inline editable slots */}
                <div className="rounded-md bg-zinc-950 border border-zinc-800 p-3 text-[13px] leading-relaxed text-zinc-200 whitespace-pre-wrap">
                  {aisensyConn.template_parts.map((p, i) => {
                    if (p.type === "text") return <span key={i}>{p.value}</span>;
                    const slotIdx = p.index - 1;
                    return (
                      <input
                        key={i}
                        data-testid={`aisensy-slot-${p.index}`}
                        value={aisensyParams[slotIdx] || ""}
                        onChange={(e) => {
                          const next = [...aisensyParams];
                          next[slotIdx] = e.target.value;
                          setAisensyParams(next);
                        }}
                        placeholder={`{{${p.index}}}`}
                        className="mx-0.5 inline-block min-w-[80px] max-w-[240px] bg-emerald-950/40 border-b border-emerald-500/30 text-emerald-200 px-1 py-0 focus:outline-none focus:border-emerald-400"
                        style={{ width: `${Math.max(80, (aisensyParams[slotIdx] || "").length * 8 + 20)}px` }}
                      />
                    );
                  })}
                </div>
                {aisensyPrefilling && (
                  <div className="mt-2 text-[11px] text-zinc-500 inline-flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3 animate-pulse" /> Filling variables from the conversation…
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                  {hasGhlSendScope && (
                    <button
                      data-testid="switch-to-sms"
                      onClick={() => setChannel("SMS")}
                      className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-300"
                      title="Or send an SMS via your CRM"
                    >
                      Or use SMS →
                    </button>
                  )}
                  <button
                    data-testid="send-aisensy-btn"
                    onClick={sendAisensy}
                    disabled={sending || aisensyParams.length !== aisensyConn.param_count}
                    className="ml-auto inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
                  >
                    <Send className="w-3.5 h-3.5" />
                    {sending ? "Sending…" : "Send via AiSensy"}
                  </button>
                </div>
                {sentOk && (
                  <div className="mt-2 text-[11px] text-emerald-400 uppercase tracking-[0.18em] inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Sent — logged in the timeline and CRM notes.
                  </div>
                )}
              </div>
            )}
          </section>

          {scoreRow?.scored_at && (
            <div className="text-[11px] text-zinc-600 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> Scored {new Date(scoreRow.scored_at).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
